import { TableCell, TableRow, Typography } from "@mui/material";
import { type Dispatch, memo, type SetStateAction, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { TimeRangeListField } from "../../../../components/UIToolkit/Inputs/TimeRangeListField";
import { TokenListField } from "../../../../components/UIToolkit/Inputs/TokenListField";
import {
	BORDERLESS_AUTOCOMPLETE_SX,
	BORDERLESS_PICKER_SX,
	CONFIG_TABLE_BODY_CELL_SX,
	CONFIG_TABLE_BODY_ROW_SX,
} from "../../../../components/UIToolkit/Tables/ConfigTable/configTableSx";
import { INK } from "../../../../theme/tokens";
import { type ShiftTimesScope, useShiftTimesWriters } from "./shiftTimes";
import type { GeneralFormData } from "./types";

/**
 * One row of the role-keyed Shift Lengths table.
 *
 * This table survives for the **role mode** branch only (`jobMode === false`), where shift lengths
 * are keyed by `NurseRole` and a jobs-keyed grid cannot represent them. In job mode the same two
 * controls live inside the Jobs grid's per-job panel (`ShiftTimesEditor`), which shares this row's
 * writers so the two surfaces cannot drift (DAT-804).
 */
export const ShiftLengthsTableRow = memo<{
	/** Role short code in role mode, job id in job mode. */
	rowKey: string;
	/** What the first column shows — the row key itself for roles, the display name for jobs. */
	label: string;
	mode: ShiftTimesScope;
	hours: number[];
	fullShiftTimes: [start: number, end: number][];
	/** Partial lengths configured elsewhere in the table, offered as dropdown options. */
	hourOptions: string[];
	setFormData: Dispatch<SetStateAction<GeneralFormData>>;
	/** Rendered under the label when a configured job is no longer enabled for the department. */
	hint?: string;
	/**
	 * Locks both cells. Set for a job the department no longer enables: the save path filters those
	 * rows out (and the backend rejects them), so leaving them editable discards the edit in silence.
	 */
	readOnly?: boolean;
}>(({ rowKey, label, mode, hours, fullShiftTimes, hourOptions, setFormData, hint, readOnly = false }) => {
	const { t } = useTranslation();
	const { commitPartialBlockShifts, setFullShiftTimes } = useShiftTimesWriters({ scope: mode, rowKey, hours, setFormData });

	const partialInvalidMessage = useCallback(
		(rejected: string[]) => t("department.config.shifts.partialShiftLengths.invalid", { values: rejected.join(", ") }),
		[t],
	);

	return (
		<TableRow sx={CONFIG_TABLE_BODY_ROW_SX}>
			<TableCell sx={[CONFIG_TABLE_BODY_CELL_SX, { verticalAlign: "middle", fontWeight: 500 }]}>
				{label}
				{hint && (
					<Typography component="span" sx={{ display: "block", fontSize: "0.75rem", fontWeight: 400, color: INK.MUTED }}>
						{hint}
					</Typography>
				)}
			</TableCell>
			<TableCell sx={[CONFIG_TABLE_BODY_CELL_SX, { width: "50%" }]}>
				<TokenListField
					values={hours.map((hour) => hour.toString())}
					options={hourOptions}
					onCommit={commitPartialBlockShifts}
					invalidMessage={partialInvalidMessage}
					placeholder={t("department.config.shifts.partialShiftLengths.placeholder")}
					disabled={readOnly}
					fieldSx={BORDERLESS_AUTOCOMPLETE_SX}
					// Keyed by rowKey (role short code or job id), so a test can target one row's field
					// without depending on render order.
					dataTestId={`dept-config-shifts-lengths-partial-${rowKey}`}
				/>
			</TableCell>
			<TableCell sx={[CONFIG_TABLE_BODY_CELL_SX, { width: "50%" }]}>
				<TimeRangeListField
					value={fullShiftTimes}
					onChange={setFullShiftTimes}
					disabled={readOnly}
					fieldSx={BORDERLESS_PICKER_SX}
					dataTestId={`dept-config-shifts-lengths-full-${rowKey}`}
					ampm={false}
				/>
			</TableCell>
		</TableRow>
	);
});
